import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ChevronRight, Flame, Mic, MonitorPlay, Play, Plus, Utensils } from 'lucide-react';

import { PartnerBanner } from './PartnerBanner';
import '../../styles/pages/inicio.css';

const home = (t, key, options) => t(`pages.Dashboard.home.${key}`, options);

// The week ring is drawn on a 120 box, so one SVG scales from 108px on phones to 168px on desktop, stroke included
const RING_BOX = 120;
const RING_STROKE = 12;
const RING_RADIUS = (RING_BOX - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const WeekRing = ({ progress }) => (
  <svg className="mt-dash-ring__svg" viewBox={`0 0 ${RING_BOX} ${RING_BOX}`} aria-hidden="true">
    <circle
      className="mt-dash-ring__track"
      cx={RING_BOX / 2}
      cy={RING_BOX / 2}
      r={RING_RADIUS}
      fill="none"
      strokeWidth={RING_STROKE}
    />
    {progress > 0 && (
      <circle
        className="mt-dash-ring__arc"
        cx={RING_BOX / 2}
        cy={RING_BOX / 2}
        r={RING_RADIUS}
        fill="none"
        strokeWidth={RING_STROKE}
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
        style={{ '--mt-dash-ring-full': RING_CIRCUMFERENCE }}
      />
    )}
  </svg>
);

const SectionHead = ({ id, title, link }) => (
  <div className="mt-dash-head">
    <h2 id={id} className="mt-dash-head__title">
      {title}
    </h2>
    {link}
  </div>
);

const HeadLink = ({ to, label, onClick }) => (
  <Link className="mt-dash-link" to={to} onClick={onClick}>
    {label}
    <ChevronRight aria-hidden="true" />
  </Link>
);

// Seven bars of the week's volume; each is a button that shows its day's session under the chart
const WeekChart = ({ days, initialDay, onSelectDay }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(initialDay);
  const day = days[selected] || days[0];

  return (
    <div className="mt-dash-chart">
      <div className="mt-dash-chart__bars" role="group" aria-label={home(t, 'chartLabel')}>
        {days.map((bar, index) => (
          <button
            key={bar.key}
            type="button"
            className={`mt-dash-bar${bar.isDone ? ' is-done' : bar.isToday ? ' is-today' : ''}`}
            aria-pressed={index === selected}
            aria-label={bar.name}
            style={{ '--mt-dash-bar': `${bar.height}%`, '--mt-dash-bar-delay': `${index * 40}ms` }}
            onClick={() => {
              setSelected(index);
              onSelectDay();
            }}
          >
            <span className="mt-dash-bar__fill" />
            {index === selected && bar.isDone && bar.volume && <span className="mt-dash-bar__tip">{bar.volume}</span>}
          </button>
        ))}
      </div>
      <div className="mt-dash-chart__days" aria-hidden="true">
        {days.map((bar) => (
          <span key={bar.key} className={bar.isToday ? 'is-today' : undefined}>
            <span className="mt-dash-chart__short">{bar.short}</span>
            <span className="mt-dash-chart__letter">{bar.letter}</span>
          </span>
        ))}
      </div>
      <p className="mt-dash-chart__caption" aria-live="polite">
        <b>{day.name}</b> <span>{day.summary}</span>
      </p>
    </div>
  );
};

// The week: the ring (sessions against the plan), the streak, volume and time, then the days. All of it opens
// Progresso, which phones reach only from here.
const WeekCard = ({ week, streak, volume, time, days, selectedDay, onNavigate, onSelectDay }) => {
  const { t } = useTranslation();

  return (
    <section className="mt-dash-card mt-dash-week" aria-labelledby="mt-dash-week-title">
      <SectionHead
        id="mt-dash-week-title"
        title={home(t, 'weekTitle')}
        link={<HeadLink to="/progress" label={home(t, 'progress')} onClick={() => onNavigate('progress')} />}
      />
      <div className="mt-dash-week__sum">
        <Link
          className="mt-dash-ring"
          to="/progress"
          aria-label={home(t, 'weekRing', { done: week.sessions, target: week.target })}
          onClick={() => onNavigate('stat_week')}
        >
          <WeekRing progress={week.progress} />
          <span className="mt-dash-ring__in" aria-hidden="true">
            <b>{week.sessions}</b>
            <small>
              <span className="mt-dash-ring__of">{home(t, 'ringOf', { target: week.target })}</span>
              <span className="mt-dash-ring__of-long">{home(t, 'ringOfLong', { target: week.target })}</span>
            </small>
          </span>
        </Link>
        <ul className="mt-dash-week__stats">
          <li>
            <Link className="mt-dash-stat" to="/progress" onClick={() => onNavigate('stat_streak')}>
              <b>{streak}</b>
              <span>{home(t, 'streakLabel')}</span>
            </Link>
          </li>
          <li>
            <Link className="mt-dash-stat" to="/progress" onClick={() => onNavigate('stat_volume')}>
              <b>
                {volume.value} <small>{volume.unit}</small>
              </b>
              <span>{home(t, 'volumeLabel')}</span>
            </Link>
          </li>
          {/* Time trained only fits on wide desktops */}
          <li className="mt-dash-week__time">
            <Link className="mt-dash-stat" to="/progress" onClick={() => onNavigate('stat_time')}>
              <b>
                {time.map((part, index) => (
                  <span key={part.unit}>
                    {index > 0 && ' '}
                    {part.value} <small>{part.unit}</small>
                  </span>
                ))}
              </b>
              <span>{home(t, 'timeLabel')}</span>
            </Link>
          </li>
        </ul>
      </div>
      <WeekChart days={days} initialDay={selectedDay} onSelectDay={onSelectDay} />
    </section>
  );
};

// No workout yet: the empty ring is the invitation to create one
const WeekInvite = ({ sessions, onNavigate }) => {
  const { t } = useTranslation();

  return (
    <section className="mt-dash-card mt-dash-week mt-dash-week--first" aria-labelledby="mt-dash-week-title">
      <SectionHead
        id="mt-dash-week-title"
        title={home(t, 'weekTitle')}
        link={<HeadLink to="/progress" label={home(t, 'progress')} onClick={() => onNavigate('progress')} />}
      />
      <div className="mt-dash-week__sum">
        <span className="mt-dash-ring">
          <WeekRing progress={0} />
          <span className="mt-dash-ring__in">
            <b>{sessions}</b>
            <small>{home(t, 'ringCount', { count: sessions })}</small>
          </span>
        </span>
        <div className="mt-dash-first">
          <h3 className="mt-dash-first__title">{home(t, 'noWorkoutTitle')}</h3>
          <p className="mt-dash-first__body">{home(t, 'noWorkoutBody')}</p>
          <Link className="mt-btn mt-btn--primary mt-dash-first__create" to="/workout">
            <Plus aria-hidden="true" />
            {home(t, 'createWorkout')}
          </Link>
        </div>
      </div>
    </section>
  );
};

const WeekSkeleton = () => (
  <section className="mt-dash-card mt-dash-week mt-dash-week--skeleton" aria-hidden="true">
    <span className="mt-skel mt-dash-skel-title" />
    <div className="mt-dash-week__sum">
      <span className="mt-dash-ring mt-skel" />
      <span className="mt-dash-skel-lines">
        <span className="mt-skel mt-dash-skel-stat" />
        <span className="mt-skel mt-dash-skel-stat" />
      </span>
    </div>
  </section>
);

// Today's workout, compact: its thumbnails, what it works, and the guided start
export const TodayCard = ({ today, onStart, onOpen }) => {
  const { t } = useTranslation();
  const blanks = today.thumbnails.length ? 4 - today.thumbnails.length : 0;

  return (
    <section
      className={`mt-dash-card mt-dash-today${today.thumbnails.length ? '' : ' mt-dash-today--bare'}`}
      aria-label={home(t, 'todayLabel')}
    >
      <div className="mt-dash-today__text">
        <div className="mt-dash-today__head">
          {/* The title's link covers the whole card (see .mt-dash-today__link); only the start button sits above it.
              It opens the workout in Meus Treinos, where it starts or goes to the editor */}
          <h2 className="mt-dash-today__title">
            <Link className="mt-dash-today__link" to={`/workouts?w=${today.id}`} onClick={onOpen}>
              {today.title}
            </Link>
          </h2>
          <span className="mt-dash-pill">{home(t, 'today')}</span>
        </div>
        {today.muscles && <p className="mt-dash-today__muscles">{today.muscles}</p>}
        <p className="mt-dash-today__meta">
          {home(t, 'exercises', { count: today.exerciseCount })} · {home(t, 'sets', { count: today.setCount })} ·{' '}
          {home(t, 'minutes', { count: today.minutes })}
        </p>
      </div>
      {today.thumbnails.length > 0 && (
        <ul className="mt-dash-today__thumbs">
          {today.thumbnails.map((thumb) => (
            <li key={thumb.key} className="mt-dash-art">
              <img src={thumb.src} alt={thumb.alt} loading="lazy" />
            </li>
          ))}
          {/* The phone's 2 × 2 mosaic keeps its shape with fewer than four exercises */}
          {Array.from({ length: blanks }, (_, index) => (
            <li key={`blank-${index}`} className="mt-dash-art mt-dash-art--blank" aria-hidden="true" />
          ))}
        </ul>
      )}
      <button type="button" className="mt-btn mt-btn--primary mt-dash-today__start" onClick={onStart}>
        <Play aria-hidden="true" />
        {home(t, 'start')}
      </button>
    </section>
  );
};

export const TodaySkeleton = () => (
  <section className="mt-dash-card mt-dash-today mt-dash-today--skeleton" aria-hidden="true">
    <div className="mt-dash-today__text">
      <span className="mt-skel mt-dash-skel-title" />
      <span className="mt-skel mt-dash-skel-line" />
      <span className="mt-skel mt-dash-skel-meta" />
    </div>
    <ul className="mt-dash-today__thumbs">
      {[0, 1, 2, 3].map((key) => (
        <li key={key} className="mt-skel" />
      ))}
    </ul>
    <span className="mt-skel mt-dash-today__start" />
  </section>
);

// Today's food: the diet plan's target and what's logged in Calorias do dia, with a way into each
const FoodCard = ({ diet, isLoading, onNavigate }) => {
  const { t } = useTranslation();

  let body = <p className="mt-dash-food__empty">{home(t, 'foodEmpty')}</p>;
  if (isLoading) {
    body = (
      <div className="mt-dash-food__loading" aria-hidden="true">
        <span className="mt-skel mt-dash-skel-kcal" />
        <span className="mt-skel mt-dash-skel-bar" />
      </div>
    );
  } else if (diet) {
    const leftKey = { left: 'foodLeft', over: 'foodOver', done: 'foodDone' }[diet.status];
    body = (
      <>
        <p className="mt-dash-food__kcal">
          <b>{diet.eaten}</b> <span>{home(t, 'foodOf', { target: diet.target })}</span>
        </p>
        <span className="mt-dash-food__bar" aria-hidden="true">
          <span style={{ width: `${Math.min(diet.progress, 1) * 100}%` }} />
        </span>
        <p className="mt-dash-food__under">
          <span>{home(t, 'foodShare', { percent: diet.percent })}</span>
          <span>
            <Trans i18nKey={`pages.Dashboard.home.${leftKey}`} values={{ kcal: diet.left }} components={{ b: <b /> }} />
          </span>
        </p>
      </>
    );
  }

  return (
    <section className="mt-dash-card mt-dash-food" aria-labelledby="mt-dash-food-title" aria-busy={isLoading || undefined}>
      <SectionHead id="mt-dash-food-title" title={home(t, 'food')} />
      {body}
      <div className="mt-dash-food__actions">
        <Link className="mt-btn mt-btn--quiet mt-dash-food__action" to="/calorie-tracker" onClick={() => onNavigate('calories')}>
          <Flame aria-hidden="true" />
          {home(t, 'logMeal')}
        </Link>
        <Link className="mt-btn mt-btn--quiet mt-dash-food__action" to="/diet" onClick={() => onNavigate('diet')}>
          <Utensils aria-hidden="true" />
          {diet || isLoading ? home(t, 'seePlan') : home(t, 'buildPlan')}
        </Link>
      </div>
    </section>
  );
};

// Meus treinos in the order they're trained, today's first, each with its letter and when it was last done
const WorkoutsList = ({ workouts, onOpen }) => {
  const { t } = useTranslation();

  return (
    <section className="mt-dash-section mt-dash-workouts" aria-labelledby="mt-dash-workouts-title">
      <SectionHead
        id="mt-dash-workouts-title"
        title={home(t, 'myWorkouts')}
        link={<HeadLink to="/workouts" label={home(t, 'seeAll')} />}
      />
      <ul className="mt-dash-card mt-dash-rows">
        {workouts.map((workout) => (
          <li key={workout.id}>
            <Link className="mt-dash-row" to={`/workouts?w=${workout.id}`} onClick={() => onOpen(workout.id)}>
              <span className={`mt-dash-letter${workout.isNext ? ' is-next' : ''}`} aria-hidden="true">
                {workout.letter}
              </span>
              <span className="mt-dash-row__text">
                <span className="mt-dash-row__title">
                  <span className="mt-dash-row__name">{workout.title}</span>
                  {workout.isNext && <span className="mt-dash-pill mt-dash-pill--small">{home(t, 'today')}</span>}
                </span>
                {(workout.muscles || workout.lastDone) && (
                  <span className="mt-dash-row__sub">{[workout.muscles, workout.lastDone].filter(Boolean).join(' · ')}</span>
                )}
              </span>
              <ChevronRight className="mt-dash-row__chevron" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

const WorkoutsSkeleton = () => {
  const { t } = useTranslation();

  return (
    <section className="mt-dash-section mt-dash-workouts" aria-hidden="true">
      <SectionHead title={home(t, 'myWorkouts')} />
      <ul className="mt-dash-card mt-dash-rows">
        {[0, 1, 2].map((key) => (
          <li key={key}>
            <span className="mt-dash-row mt-dash-row--skeleton">
              <span className="mt-skel mt-dash-letter" />
              <span className="mt-skel mt-dash-skel-row" />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

// The shortcuts left once Progresso, the diet and the calories live in their cards; Alexa first (owner, 26/10/2026)
const MoreLinks = ({ onNavigate }) => {
  const { t } = useTranslation();
  const items = [
    { key: 'alexa', to: '/sync', Icon: Mic, title: home(t, 'alexa'), sub: home(t, 'alexaSub') },
    { key: 'academy', to: '/academy', Icon: MonitorPlay, title: home(t, 'academy'), sub: home(t, 'academySub') },
  ];

  return (
    <nav className="mt-dash-section mt-dash-more" aria-labelledby="mt-dash-more-title">
      <SectionHead id="mt-dash-more-title" title={home(t, 'more')} />
      <ul className="mt-dash-card mt-dash-rows">
        {items.map(({ key, to, Icon, title, sub }) => (
          <li key={key}>
            <Link className={`mt-dash-row mt-dash-row--${key}`} to={to} onClick={() => onNavigate(key)}>
              <Icon className="mt-dash-row__icon" aria-hidden="true" />
              <span className="mt-dash-row__text">
                <span className="mt-dash-row__title">{title}</span>
                <span className="mt-dash-row__sub">{sub}</span>
              </span>
              <ChevronRight className="mt-dash-row__chevron" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// Presentational dashboard (approved "B · Resumo", 29/10/2026): all data arrives already computed from Dashboard.jsx
export const DashboardView = ({
  greetingName,
  dateLine,
  today,
  week,
  streak,
  volume,
  time,
  days,
  selectedDay,
  workouts,
  diet,
  onStart,
  onOpenToday,
  onOpenWorkout,
  onSelectDay,
  onNavigate,
  onOpenPartner,
  loading = {},
}) => {
  const { t } = useTranslation();
  // No workout yet: the week card invites to create one, and the food card takes today's place beside it
  const isFirst = !loading.today && !today;

  let weekCard = <WeekSkeleton />;
  if (!loading.today) {
    weekCard = today ? (
      <WeekCard
        week={week}
        streak={streak}
        volume={volume}
        time={time}
        days={days}
        selectedDay={selectedDay}
        onNavigate={onNavigate}
        onSelectDay={onSelectDay}
      />
    ) : (
      <WeekInvite sessions={week.sessions} onNavigate={onNavigate} />
    );
  }

  return (
    <div className={`mt-dash${isFirst ? ' mt-dash--first' : ''}`} aria-busy={loading.today || loading.name ? 'true' : undefined}>
      <div className="mt-dash-hello">
        <h1 className="mt-dash-hello__title">
          {loading.name ? <span className="mt-skel mt-dash-skel-hello" aria-hidden="true" /> : t('pages.Dashboard.greeting', { name: greetingName })}
        </h1>
        {dateLine && <p className="mt-dash-hello__date">{dateLine}</p>}
      </div>
      {weekCard}
      {loading.today ? <TodaySkeleton /> : today && <TodayCard today={today} onStart={onStart} onOpen={onOpenToday} />}
      <FoodCard diet={diet} isLoading={loading.diet} onNavigate={onNavigate} />
      {loading.today ? <WorkoutsSkeleton /> : workouts.length > 0 && <WorkoutsList workouts={workouts} onOpen={onOpenWorkout} />}
      <div className="mt-dash-side">
        <MoreLinks onNavigate={onNavigate} />
        <PartnerBanner onOpen={onOpenPartner} />
      </div>
    </div>
  );
};
